import { BaseService } from "./BaseService";
import {
	Collection,
	Document,
	QueryOptions,
	ErrorResponse,
	SuccessResponse,
	ImportMapping,
	BuildxConfig,
} from "../types/index";

/**
 * Collections service for Buildx
 * Handles collection management, documents, and queries
 * 
 * @example
 * ```typescript
 * const collections = buildx.collections();
 * 
 * // Get all collections
 * const collectionsList = await collections.list();
 * 
 * // Get collection schema
 * const schema = await collections.getSchema('collection-id');
 * 
 * // Query documents
 * const docs = await collections.query('collection-id', {
 *   filter: { status: 'active' },
 *   limit: 10
 * });
 * ```
 */
export class Collections {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * List all collections in a project
	 */
	async list(projectId?: string, withStats = false, withBuildx = false): Promise<Collection[] | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/collections");
		const queryParams: Record<string, any> = {};

		if (withStats) {
			queryParams.with_stats = true;
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		const collections = await this.baseService.get(fullUrl);

		if (Array.isArray(collections) && !withBuildx) {
			return collections.filter(collection =>
				collection.collection_id && !collection.collection_id.startsWith("buildx_")
			);
		}

		return collections;
	}

	/**
	 * Get collection schema
	 */
	async getSchema(collectionId: string, projectId?: string, depth = 1): Promise<Collection | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/collection/${collectionId}`);
		const queryParams: Record<string, any> = {};

		if (depth > 1) {
			queryParams.depth = depth;
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		return await this.baseService.get(fullUrl);
	}

	/**
	 * Create or update collection
	 */
	async set(collectionData: Partial<Collection>, projectId?: string): Promise<Collection | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/collections");
		return await this.baseService.post(url, collectionData);
	}

	/**
	 * Delete collection
	 */
	async deleteCollection(collectionId: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/collections/${collectionId}`);
		return await this.baseService.delete(url);
	}

	/**
	 * Query documents in a collection
	 */
	async query(collectionId: string, options?: QueryOptions, projectId?: string): Promise<Document[] | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}`);
		const queryParams: Record<string, any> = {};

		if (options) {
			if (options.filter) {
				queryParams.filter = options.filter;
			}
			if (options.jsonFilter) {
				queryParams.jsonFilter = options.jsonFilter;
			}
			if (options.select) {
				queryParams.select = Array.isArray(options.select)
					? options.select.join(",")
					: options.select;
			}
			if (options.projection) {
				queryParams.projection = options.projection;
			}
			if (options.options) {
				queryParams.options = options.options;
			}
			if (options.sort) {
				queryParams.sort = options.sort;
			}
			if (options.limit) {
				queryParams.limit = options.limit;
			}
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		return await this.baseService.get(fullUrl);
	}

	/**
	 * Get lookup data for a collection (for dropdowns/autocomplete)
	 */
	async lookup(collectionId: string, options?: QueryOptions, projectId?: string): Promise<Array<{ value: string; label: string }> | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}`);
		const queryParams: Record<string, any> = {};

		if (options) {
			if (options.filter) {
				queryParams.filter = options.filter;
			}
			if (options.jsonFilter) {
				queryParams.jsonFilter = options.jsonFilter;
			}
			if (options.projection) {
				queryParams.projection = options.projection;
			}
			if (options.options) {
				queryParams.options = options.options;
			}
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		const result = await this.baseService.get(fullUrl);

		if (Array.isArray(result)) {
			return result.map(row => ({
				value: row._id,
				label: row._display || row.label || row.title || row.name || row.username || row.description || ""
			}));
		}

		return result;
	}

	/**
	 * Get single document by ID
	 */
	async getDocument(collectionId: string, documentId: string, populate?: string[], projectId?: string): Promise<Document | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/${documentId}`);
		const queryParams: Record<string, any> = {};

		if (populate && populate.length > 0) {
			queryParams.populate = populate.join(",");
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		return await this.baseService.get(fullUrl);
	}

	/**
	 * Create new document
	 */
	async createDocument(collectionId: string, data: any, projectId?: string): Promise<Document | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}`);
		return await this.baseService.post(url, data);
	}

	/**
	 * Update document
	 */
	async updateDocument(collectionId: string, documentId: string, data: any, projectId?: string): Promise<Document | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/${documentId}`);
		return await this.baseService.patch(url, data);
	}

	/**
	 * Delete document
	 */
	async deleteDocument(collectionId: string, documentId: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/${documentId}`);
		return await this.baseService.delete(url);
	}

	/**
	 * Delete documents by filter
	 */
	async deleteByFilter(collectionId: string, filter: any, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/delete`);
		return await this.baseService.post(url, { filter });
	}

	/**
	 * Import data to collection
	 */
	async import(collectionId: string, file: File, mapping: ImportMapping, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const formData = new FormData();
		formData.append("mapping", JSON.stringify(mapping));
		formData.append("file", file);

		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/import`);
		const headers = { ...(await this.baseService.getHeaders()), "Content-Type": "multipart/form-data" };

		return await this.baseService.post(url, formData, { headers });
	}

	/**
	 * Get collection data types
	 */
	async getDataTypes(projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/collections/types");
		return await this.baseService.get(url);
	}

	/**
	 * Validate collection data
	 */
	async validate(collectionId: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/validate`);
		return await this.baseService.post(url, null);
	}

	/**
	 * Migrate collection data
	 */
	async migrate(collectionId: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/${collectionId}/migrate`);
		return await this.baseService.post(url, null);
	}
} 